import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Select,
  message,
  Dropdown,
  Modal,
  Card,
  List,
  Progress,
  Drawer,
  Typography,
  Upload,
  Checkbox,
  Pagination,
  Spin
} from 'antd';
import type { MenuProps } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FolderOutlined,
  DownOutlined,
  MoreOutlined,
  SettingOutlined,
  ImportOutlined,
  UploadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  literatureService,
  copyLiteratureCslExportToClipboard,
  Literature,
  LiteratureStorageSummary,
  literatureHasPdfAttachment,
  type BibliographyImportEntryPreview,
  type UserCslStyleInfo
} from '../../services/literature/literatureService';
import { categoryService, Category } from '../../services/literature/categoryService';
import CategoryManager from '../../components/literature/CategoryManager';
import LiteratureInspectorPanel from '../../components/literature/LiteratureInspectorPanel';
import UserCslStyleModal from '../../components/literature/UserCslStyleModal';
import { LiteratureLimsShell, type LiteratureCrumbItem } from '../../components/literature/LiteratureLimsShell';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { randomClientId } from '../../utils/clientRandomId';
import { useAuth } from '../../contexts/AuthContext';
import { filterDemoLiteratures, getDemoLiteratures } from '../../demo/demoBrowseMocks';
import '../../styles/literature-stitch.css';

function formatLiteratureRelativeTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const t = d.getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  return d.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
}

/** 列表卡片正文预览：展示笔记（替代原摘要位） */
function literatureListCardNotesPreview(record: Literature): string {
  const raw = (record.notes || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return raw.slice(0, 220);
}

function readStatusPillClass(status: string | undefined): string {
  switch (status) {
    case '未读':
      return 'lit-lib-pill lit-lib-pill--muted';
    case '阅读中':
      return 'lit-lib-pill lit-lib-pill--reading';
    case '已读':
      return 'lit-lib-pill lit-lib-pill--clinical';
    case '精读':
      return 'lit-lib-pill lit-lib-pill--protocol';
    default:
      return 'lit-lib-pill lit-lib-pill--muted';
  }
}

/** 导入预览表格行：带客户端唯一键，不落库 */
type BibImportPreviewRow = BibliographyImportEntryPreview & { _previewKey: string };

const { Option } = Select;

function readFileAsUtf8(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
    r.onerror = () => reject(new Error('读取文件失败'));
    r.readAsText(file, 'UTF-8');
  });
}

const LiteratureList: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { categoryId } = useParams<{ categoryId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilterValue = categoryId ?? '';

  const [literatures, setLiteratures] = useState<Literature[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    keyword: '',
    readStatus: '',
    sortBy: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc'
  });
  const [categoryManagerVisible, setCategoryManagerVisible] = useState(false);
  const [storageSummary, setStorageSummary] = useState<LiteratureStorageSummary | null>(null);
  const [inspectorSelectedId, setInspectorSelectedId] = useState<string | null>(null);
  const [inspectorDrawerOpen, setInspectorDrawerOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const importFilesRef = useRef<File[]>([]);
  /** 同一轮文件选择器多选时，antd 会逐文件触发 beforeUpload，用微任务合并为一批 */
  const importSelectBatchRef = useRef<File[]>([]);
  const importSelectFlushScheduledRef = useRef(false);
  const [importFormat, setImportFormat] = useState<'auto' | 'bibtex' | 'ris'>('auto');
  const [importCategoryId, setImportCategoryId] = useState<string | undefined>(undefined);
  const [previewEntries, setPreviewEntries] = useState<BibImportPreviewRow[]>([]);
  const [previewFormat, setPreviewFormat] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importCommitLoading, setImportCommitLoading] = useState(false);
  const [importFileLabel, setImportFileLabel] = useState<string | null>(null);
  const [importFileCount, setImportFileCount] = useState(0);
  const [userCslStyles, setUserCslStyles] = useState<UserCslStyleInfo[]>([]);
  const [cslModalOpen, setCslModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isDemoBrowseOnly } = useAuth();

  const categoryFilterOptions = useMemo(
    () => [
      { value: '', label: '全部文献' },
      { value: 'unfiled', label: '未分类' },
      ...categories.map((c) => ({ value: c._id, label: c.name }))
    ],
    [categories]
  );

  const handleCategoryFilterChange = useCallback(
    (val: string | null | undefined) => {
      setPagination((p) => ({ ...p, page: 1 }));
      const v = val ?? '';
      if (!v) navigate('/literature');
      else if (v === 'unfiled') navigate('/literature/category/unfiled');
      else navigate(`/literature/category/${v}`);
    },
    [navigate]
  );

  const refreshUserCslStyles = useCallback(async () => {
    try {
      const list = await literatureService.listUserCslStyles();
      setUserCslStyles(Array.isArray(list) ? list : []);
    } catch {
      setUserCslStyles([]);
    }
  }, []);

  useEffect(() => {
    void refreshUserCslStyles();
  }, [refreshUserCslStyles]);

  const refreshStorageSummary = useCallback(async () => {
    try {
      const s = await literatureService.getStorageSummary();
      setStorageSummary(s);
    } catch {
      /* 列表页不因用量接口失败打断 */
    }
  }, []);

  // 获取分类列表
  const fetchCategories = async () => {
    try {
      const list = await categoryService.getList();
      setCategories(list);
    } catch (error) {
      console.error('获取分类列表失败:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    
    // 监听分类更新事件
    const handleCategoryUpdate = () => {
      fetchCategories();
    };
    
    window.addEventListener('categoryUpdated', handleCategoryUpdate);
    
    return () => {
      window.removeEventListener('categoryUpdated', handleCategoryUpdate);
    };
  }, []);

  const fetchLiteratures = useCallback(async () => {
    setLoading(true);
    try {
      if (isDemoBrowseOnly) {
        const all = filterDemoLiteratures(
          getDemoLiteratures(),
          categoryId === 'unfiled' ? 'unfiled' : categoryId || undefined,
          filters.keyword
        );
        const sorted = [...all].sort((a, b) => {
          const dir = filters.sortOrder === 'asc' ? 1 : -1;
          if (filters.sortBy === 'title') {
            return a.title.localeCompare(b.title, 'zh-CN') * dir;
          }
          const at = new Date(a.createdAt).getTime();
          const bt = new Date(b.createdAt).getTime();
          return (at - bt) * dir;
        });
        const start = (pagination.page - 1) * pagination.limit;
        const slice = sorted.slice(start, start + pagination.limit);
        setLiteratures(slice);
        setPagination((prev) => ({ ...prev, total: sorted.length }));
        return;
      }
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      // 文件夹：未分类路由为 /literature/category/unfiled
      if (categoryId === 'unfiled') {
        params.categoryId = 'null';
      } else if (categoryId) {
        params.categoryId = categoryId;
      }

      const result = await literatureService.getList(params);
      setLiteratures(result.literatures);
      setPagination(prev => ({
        ...prev,
        total: result.pagination.total
      }));
      refreshStorageSummary();
    } catch (error: any) {
      message.error(error.message || '获取文献列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, categoryId, refreshStorageSummary, isDemoBrowseOnly]);

  useEffect(() => {
    fetchLiteratures();
    setSelectedRowKeys([]);
  }, [fetchLiteratures]);

  useEffect(() => {
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [categoryId]);

  useEffect(() => {
    setInspectorSelectedId(null);
    setInspectorDrawerOpen(false);
  }, [categoryId]);

  const inspectParam = searchParams.get('inspect');
  useEffect(() => {
    if (!inspectParam) return;
    setInspectorSelectedId(inspectParam);
    setInspectorDrawerOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('inspect');
        return next;
      },
      { replace: true }
    );
  }, [inspectParam, setSearchParams]);

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, keyword: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id: string) => {
    try {
      await literatureService.delete(id);
      message.success('删除成功');
      fetchLiteratures();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  // 更新阅读状态
  const handleUpdateReadStatus = async (id: string, readStatus: string) => {
    try {
      await literatureService.update(id, { readStatus } as any);
      message.success('阅读状态已更新');
      fetchLiteratures();
    } catch (error: any) {
      message.error(error.message || '更新阅读状态失败');
    }
  };

  /** 在内存中读取一个或多个文件并请求解析（不展示原文）；多文件条目合并预览 */
  const parseImportFiles = useCallback(
    async (files: File[], fmtSel?: 'auto' | 'bibtex' | 'ris') => {
      if (!files.length) {
        setPreviewEntries([]);
        setPreviewFormat('');
        setImportFileLabel(null);
        setImportFileCount(0);
        return;
      }
      setPreviewLoading(true);
      setPreviewEntries([]);
      setPreviewFormat('');
      const label =
        files.length === 1
          ? files[0].name
          : `${files.length} 个文件：${files.map((f) => f.name).join('、')}`;
      setImportFileLabel(label);
      try {
        const allEntries: BibImportPreviewRow[] = [];
        const formats = new Set<string>();
        const fileErrors: string[] = [];
        let okFiles = 0;

        for (const file of files) {
          try {
            const text = await readFileAsUtf8(file);
            if (!text.trim()) {
              fileErrors.push(`${file.name}：文件为空`);
              continue;
            }
            const lower = file.name.toLowerCase();
            let fmt = fmtSel ?? importFormat;
            if (fmt === 'auto') {
              if (lower.endsWith('.ris')) fmt = 'ris';
              else if (lower.endsWith('.bib') || lower.endsWith('.bibtex')) fmt = 'bibtex';
              else fmt = 'auto';
            }
            const res = await literatureService.importPreview(text, fmt);
            formats.add(res.format);
            for (const e of res.entries) {
              allEntries.push({ ...e, _previewKey: randomClientId() });
            }
            okFiles += 1;
          } catch (err: any) {
            fileErrors.push(`${file.name}：${err?.message || '解析失败'}`);
          }
        }

        setPreviewEntries(allEntries);
        const formatList = Array.from(formats);
        const formatSummary =
          formatList.length === 0
            ? ''
            : formatList.length === 1
              ? formatList[0]
              : `混合（${formatList.map((f) => f.toUpperCase()).join(' + ')}）`;
        setPreviewFormat(formatSummary);

        if (allEntries.length > 0) {
          const fmtHint =
            formatList.length === 1
              ? `（${formatList[0].toUpperCase()}）`
              : formatList.length > 1
                ? '（多格式）'
                : '';
          message.success(
            files.length === 1
              ? `已解析 ${allEntries.length} 条${fmtHint}`
              : `已从 ${okFiles}/${files.length} 个文件解析 ${allEntries.length} 条${fmtHint}`
          );
        }
        if (fileErrors.length > 0) {
          message.warning(fileErrors.join('；'));
        }
        if (allEntries.length === 0 && fileErrors.length === 0) {
          message.warning('未解析到任何条目');
        }
      } catch (e: any) {
        message.error(e.message || '解析失败');
        setPreviewEntries([]);
        setPreviewFormat('');
      } finally {
        setPreviewLoading(false);
      }
    },
    [importFormat]
  );

  const handleBibliographyFilesSelect = async (files: File[]) => {
    importFilesRef.current = files;
    setImportFileCount(files.length);
    await parseImportFiles(files);
  };

  const handleBibliographyImportCommit = async () => {
    if (previewEntries.length === 0) {
      message.warning('请先选择并上传 RIS / BibTeX 文件');
      return;
    }
    if (previewEntries.length > 200) {
      message.warning('单次最多导入 200 条，请减少文件或分批导入');
      return;
    }
    setImportCommitLoading(true);
    try {
      const res = await literatureService.importCommit(
        previewEntries.map(({ _previewKey: _k, ...e }) => e),
        importCategoryId
      );
      if (res.errorCount > 0) {
        const detail = res.errors.map((e) => `第 ${e.index + 1} 条：${e.message}`).join('；');
        if (res.createdCount > 0) {
          message.warning(`成功 ${res.createdCount} 条，另有 ${res.errorCount} 条未导入。${detail}`);
        } else {
          message.error(`未导入任何条目。${detail}`);
        }
      } else {
        message.success(`导入完成：成功 ${res.createdCount} 条`);
      }
      if (res.createdCount > 0) {
        setImportModalOpen(false);
        fetchLiteratures();
      }
    } catch (e: any) {
      message.error(e.message || '导入失败');
    } finally {
      setImportCommitLoading(false);
    }
  };

  // 批量归类到分类
  const handleBatchCategory = async (targetCategoryId: string | null) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要归类的文献');
      return;
    }

    try {
      await literatureService.batchUpdateCategory(selectedRowKeys as string[], targetCategoryId);
      message.success('归类成功');
      setSelectedRowKeys([]);
      fetchLiteratures();
    } catch (error: any) {
      message.error(error.message || '归类失败');
    }
  };

  const handleExportDownload = useCallback(
    async (format: 'bibtex' | 'ris') => {
      if (selectedRowKeys.length === 0) {
        message.warning('请先勾选要导出的文献');
        return;
      }
      try {
        await literatureService.exportLiteraturesFile(selectedRowKeys as string[], format);
        message.success('已开始下载');
      } catch (e: any) {
        message.error(e.message || '导出失败');
      }
    },
    [selectedRowKeys]
  );

  const handleExportCopyCsl = useCallback(
    async (style: string) => {
      if (selectedRowKeys.length === 0) {
        message.warning('请先勾选要导出的文献');
        return;
      }
      try {
        const data = await literatureService.exportLiteraturesCsl(selectedRowKeys as string[], style);
        await copyLiteratureCslExportToClipboard(data);
        message.success(
          data.html?.trim()
            ? `已复制 ${data.count} 条（${style}）；粘贴到 Word 等可保留斜体等格式`
            : `已复制 ${data.count} 条参考文献（${style}）`
        );
      } catch (e: any) {
        if (e?.name === 'NotAllowedError') {
          message.error('无法访问剪贴板，请检查浏览器权限');
        } else {
          message.error(e.message || '复制失败');
        }
      }
    },
    [selectedRowKeys]
  );

  // 单个文献归类
  const handleSingleCategory = async (literatureId: string, targetCategoryId: string | null) => {
    try {
      await literatureService.batchUpdateCategory([literatureId], targetCategoryId);
      message.success('归类成功');
      fetchLiteratures();
    } catch (error: any) {
      message.error(error.message || '归类失败');
    }
  };

  // 获取当前文献的分类信息
  const getCategoryName = (categoryId: string | null | undefined) => {
    if (!categoryId) return null;
    const category = categories.find(c => c._id === categoryId);
    return category ? category.name : null;
  };

  const buildLiteratureActionMenu = (record: Literature): MenuProps['items'] => {
    const categoryMenuItems = [
      {
        key: 'null',
        label: '未分类',
        onClick: () => handleSingleCategory(record._id, null)
      },
      ...categories.map((category) => ({
        key: category._id,
        label: (
          <span>
            <FolderOutlined style={{ color: category.color, marginRight: 4 }} />
            {category.name}
          </span>
        ),
        onClick: () => handleSingleCategory(record._id, category._id)
      }))
    ];
    return [
      {
        key: 'view',
        label: '打开检视',
        icon: <EyeOutlined />,
        onClick: () => {
          setInspectorSelectedId(record._id);
          setInspectorDrawerOpen(true);
        }
      },
      ...(literatureHasPdfAttachment(record)
        ? [
            {
              key: 'viewPdf',
              label: '查看PDF',
              icon: <EyeOutlined />,
              onClick: () => navigate(`/literature/${record._id}/view`)
            }
          ]
        : []),
      {
        key: 'edit',
        label: '编辑',
        icon: <EditOutlined />,
        onClick: () => navigate(`/literature/${record._id}/edit`)
      },
      {
        key: 'category',
        label: '归类',
        icon: <FolderOutlined />,
        children: categoryMenuItems
      },
      { type: 'divider' as const },
      {
        key: 'delete',
        label: (
          <span style={{ color: '#ff4d4f' }}>
            <DeleteOutlined style={{ marginRight: 4 }} />
            删除
          </span>
        ),
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: '确定要删除这篇文献吗？',
            content: '删除后无法恢复',
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: () => handleDelete(record._id)
          });
        }
      }
    ];
  };

  const toggleCardSelected = (recordId: string, checked: boolean) => {
    setSelectedRowKeys((prev) => {
      const set = new Set(prev.map(String));
      if (checked) set.add(recordId);
      else set.delete(recordId);
      return Array.from(set);
    });
  };

  // 批量归类菜单项
  const batchCategoryMenuItems = [
    {
      key: 'null',
      label: '未分类',
      onClick: () => handleBatchCategory(null)
    },
    ...categories.map(category => ({
      key: category._id,
      label: (
        <span>
          <FolderOutlined style={{ color: category.color, marginRight: 4 }} />
          {category.name}
        </span>
      ),
      onClick: () => handleBatchCategory(category._id)
    }))
  ];

  const batchExportMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      { key: 'bib', label: '下载 BibTeX（.bib）', onClick: () => void handleExportDownload('bibtex') },
      { key: 'ris', label: '下载 RIS（.ris）', onClick: () => void handleExportDownload('ris') },
      { type: 'divider' },
      {
        key: 'gb',
        label: '复制参考文献（GB/T 7714-2015，Word 可保留格式）',
        onClick: () => void handleExportCopyCsl('gb7714-numeric')
      },
      { key: 'apa', label: '复制参考文献（APA，Word 可保留格式）', onClick: () => void handleExportCopyCsl('apa') },
      {
        key: 'van',
        label: '复制参考文献（Vancouver，Word 可保留格式）',
        onClick: () => void handleExportCopyCsl('vancouver')
      },
      {
        key: 'harv',
        label: '复制参考文献（Harvard1，Word 可保留格式）',
        onClick: () => void handleExportCopyCsl('harvard1')
      }
    ];
    if (userCslStyles.length > 0) {
      items.push({ type: 'divider', key: 'cus-div' });
      items.push({
        type: 'group',
        key: 'cus-group',
        label: '已上传的 CSL',
        children: userCslStyles.map((s) => ({
          key: `cus-${s.styleKey}`,
          label: `复制（${s.label || s.styleKey}）`,
          onClick: () => void handleExportCopyCsl(s.styleKey)
        }))
      });
    }
    items.push({ type: 'divider', key: 'manage-div' });
    items.push({
      key: 'manage-csl',
      label: '管理自定义 CSL…',
      onClick: () => setCslModalOpen(true)
    });
    return items;
  }, [userCslStyles, handleExportDownload, handleExportCopyCsl]);

  const formatStorageMb = (bytes: number) =>
    `${(bytes / (1024 * 1024)).toFixed(bytes >= 100 * 1024 * 1024 ? 1 : 2)} MB`;

  const listScopeTitle = useMemo(() => {
    if (!categoryId) return '文献库';
    if (categoryId === 'unfiled') return '未分类';
    const c = categories.find((x) => x._id === categoryId);
    return c?.name || '文献库';
  }, [categoryId, categories]);

  const literatureCrumbs = useMemo<LiteratureCrumbItem[]>(() => {
    if (!categoryId) {
      return [
        { label: '首页', path: '/dashboard' },
        { label: '文献库' }
      ];
    }
    return [
      { label: '首页', path: '/dashboard' },
      { label: '文献管理', path: '/literature' },
      { label: listScopeTitle }
    ];
  }, [categoryId, listScopeTitle]);

  const openImportModal = () => {
    importFilesRef.current = [];
    importSelectBatchRef.current = [];
    importSelectFlushScheduledRef.current = false;
    setImportFormat('auto');
    setPreviewEntries([]);
    setPreviewFormat('');
    setImportFileLabel(null);
    setImportFileCount(0);
    if (categoryId && categoryId !== 'unfiled') {
      setImportCategoryId(categoryId);
    } else {
      setImportCategoryId(undefined);
    }
    setImportModalOpen(true);
  };

  return (
    <LiteratureLimsShell
      crumbs={literatureCrumbs}
      title={listScopeTitle}
      subtitle="检索、归类、批注与参考文献导出；支持与共享文件夹、笔记本联动。"
      headActions={
        <>
          <button type="button" className="mm-btn-primary" onClick={() => navigate('/literature/new')}>
            <LimsNavIcon name="library_add" sizePx={20} />
            添加文献
          </button>
          <Button icon={<ImportOutlined />} onClick={openImportModal}>
            从 RIS/BibTeX 导入
          </Button>
        </>
      }
    >
      {storageSummary && storageSummary.quotaBytes > 0 ? (
        <div className="lit-storage-banner">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
              flexWrap: 'wrap',
              gap: 8
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--mm-on-bg)' }}>
              {storageSummary.scope === 'team' ? '团队文献存储' : '个人文献存储'}
              {storageSummary.scope === 'team' && storageSummary.teamName
                ? `（${storageSummary.teamName}）`
                : ''}
            </span>
            <span style={{ fontSize: 13, color: 'var(--mm-on-variant)' }}>
              {formatStorageMb(storageSummary.usedBytes)} / {formatStorageMb(storageSummary.quotaBytes)}
            </span>
          </div>
          <Progress
            percent={Math.min(
              100,
              Math.round((storageSummary.usedBytes / storageSummary.quotaBytes) * 100)
            )}
            strokeColor={
              storageSummary.usedBytes >= storageSummary.quotaBytes
                ? 'var(--mm-error)'
                : 'var(--mm-primary-container)'
            }
            trailColor="rgba(0, 53, 95, 0.08)"
            showInfo={false}
            size="small"
          />
          {storageSummary.scope === 'team' ? (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--mm-on-variant)' }}>
              配额为团队共用，团队成员上传的附件合计计入此用量。
            </p>
          ) : null}
        </div>
      ) : null}

      <CategoryManager
        visible={categoryManagerVisible}
        onClose={() => setCategoryManagerVisible(false)}
        onUpdate={() => {
          fetchCategories();
        }}
      />

      <UserCslStyleModal
        open={cslModalOpen}
        onClose={() => setCslModalOpen(false)}
        onStylesChanged={() => void refreshUserCslStyles()}
      />

      <Modal
        title="从 RIS / BibTeX 导入"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        width={Math.min(900, typeof window !== 'undefined' ? window.innerWidth - 32 : 900)}
        destroyOnHidden
        footer={[
          <Button key="close" onClick={() => setImportModalOpen(false)}>
            关闭
          </Button>,
          <Button
            key="import"
            type="primary"
            loading={importCommitLoading}
            disabled={previewEntries.length === 0}
            onClick={() => void handleBibliographyImportCommit()}
          >
            导入 {previewEntries.length > 0 ? `${previewEntries.length} 条` : ''}
          </Button>
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
          在 Zotero 等工具中导出为 <strong>RIS</strong> 或 <strong>BibTeX</strong> 文件，可一次选择多个文件批量解析预览。可切换「格式」后自动重新解析。导入的条目仅含书目信息，不含 PDF 附件。
          预览表「期刊」与 CSL 的 <Typography.Text code>container-title</Typography.Text> 等出版物容器题名变量对齐，由后端按序从多条 CSL 字段归并；导出引用样式时再写回 <Typography.Text code>container-title</Typography.Text>。
        </Typography.Paragraph>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Upload
              multiple
              accept=".ris,.bib,.bibtex,.txt"
              showUploadList={false}
              fileList={[]}
              beforeUpload={(file) => {
                importSelectBatchRef.current.push(file as File);
                if (!importSelectFlushScheduledRef.current) {
                  importSelectFlushScheduledRef.current = true;
                  queueMicrotask(() => {
                    const batch = importSelectBatchRef.current;
                    importSelectBatchRef.current = [];
                    importSelectFlushScheduledRef.current = false;
                    if (batch.length > 0) {
                      void handleBibliographyFilesSelect(batch);
                    }
                  });
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} loading={previewLoading}>
                选择 RIS / BibTeX 文件（可多选）
              </Button>
            </Upload>
            {importFileLabel ? (
              <Typography.Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
                已载入：{importFileLabel}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: '0 4px' }}
                  onClick={() => {
                    importFilesRef.current = [];
                    setImportFileLabel(null);
                    setImportFileCount(0);
                    setPreviewEntries([]);
                    setPreviewFormat('');
                  }}
                >
                  清除
                </Button>
              </Typography.Text>
            ) : null}
          </div>
          <Space wrap align="center">
            <span style={{ fontSize: 13 }}>格式：</span>
            <Select
              value={importFormat}
              onChange={(v) => {
                setImportFormat(v);
                if (importFilesRef.current.length > 0) {
                  void parseImportFiles(importFilesRef.current, v);
                }
              }}
              style={{ width: 140 }}
              options={[
                { value: 'auto', label: '自动识别' },
                { value: 'bibtex', label: 'BibTeX' },
                { value: 'ris', label: 'RIS' }
              ]}
            />
            <span style={{ fontSize: 13 }}>归入集合：</span>
            <Select
              allowClear
              placeholder="不指定"
              style={{ minWidth: 180 }}
              value={importCategoryId}
              onChange={(v) => setImportCategoryId(v)}
              options={categories.map((c) => ({ label: c.name, value: c._id }))}
            />
          </Space>
          {previewFormat ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              已识别为{' '}
              {previewFormat.includes('混合') ? previewFormat : previewFormat.toUpperCase()}
              {previewEntries.length > 0 ? `，共 ${previewEntries.length} 条` : ''}
              {importFileCount > 1 ? `（来自 ${importFileCount} 个文件）` : ''}
            </Typography.Text>
          ) : null}
          {previewEntries.length > 0 ? (
            <Table<BibImportPreviewRow>
              size="small"
              pagination={false}
              scroll={{ x: 720 }}
              rowKey="_previewKey"
              dataSource={previewEntries}
              columns={[
                { title: '标题', dataIndex: 'title', key: 'title', ellipsis: true, width: 200 },
                {
                  title: '作者',
                  key: 'authors',
                  ellipsis: true,
                  width: 140,
                  render: (_, r) => (r.authors || []).join('; ')
                },
                { title: '期刊', dataIndex: 'journal', key: 'journal', ellipsis: true, width: 100 },
                { title: '年', dataIndex: 'year', key: 'year', width: 52 },
                {
                  title: '卷/期/页',
                  key: 'vip',
                  ellipsis: true,
                  width: 120,
                  render: (_, r) =>
                    [r.volume, r.issue, r.pages].filter(Boolean).join(', ') || '—'
                },
                { title: '出版社', dataIndex: 'publisher', key: 'publisher', ellipsis: true, width: 100 },
                { title: 'DOI', dataIndex: 'doi', key: 'doi', ellipsis: true, width: 120 }
              ]}
            />
          ) : null}
        </Space>
      </Modal>

      <div className="mm-table-shell lit-mm-table-shell--stitch">
        <div className="mm-table-toolbar">
          <div className="lit-toolbar-inner">
            <div className="lit-toolbar-search-row">
              <div className="mm-search lit-mm-search-grow">
                <LimsNavIcon name="search" sizePx={18} className="mm-search-icon" />
                <input
                  type="search"
                  className="mm-search-input"
                  placeholder="搜索标题、作者、期刊、关键词、标签、笔记、DOI、摘要…"
                  value={filters.keyword}
                  onChange={(e) => setFilters((prev) => ({ ...prev, keyword: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch((e.target as HTMLInputElement).value);
                  }}
                  aria-label="搜索文献"
                />
              </div>
              <button
                type="button"
                className="mm-btn-primary lit-toolbar-submit"
                onClick={() => handleSearch(filters.keyword)}
              >
                <LimsNavIcon name="search" sizePx={18} />
                搜索
              </button>
            </div>
            <div className="lit-toolbar-filters">
              <Select
                className="lit-toolbar-select lit-toolbar-select--category"
                showSearch
                optionFilterProp="label"
                allowClear
                placeholder="集合 / 分类"
                title="按集合筛选文献"
                aria-label="按集合筛选文献"
                value={categoryFilterValue}
                onChange={(v) => handleCategoryFilterChange(v)}
                options={categoryFilterOptions}
                style={{ minWidth: isMobile ? 160 : 200, maxWidth: '100%' }}
              />
              <Select
                className="lit-toolbar-select"
                value={filters.readStatus}
                onChange={(value) => {
                  setFilters((prev) => ({ ...prev, readStatus: value }));
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                style={{ width: isMobile ? 140 : 132 }}
                allowClear
                placeholder="阅读状态"
              >
                <Option value="未读">未读</Option>
                <Option value="阅读中">阅读中</Option>
                <Option value="已读">已读</Option>
                <Option value="精读">精读</Option>
              </Select>
              <Select
                className="lit-toolbar-select"
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(value) => {
                  const [sortBy, sortOrder] = value.split('-');
                  setFilters((prev) => ({ ...prev, sortBy, sortOrder: sortOrder as 'asc' | 'desc' }));
                }}
                style={{ width: isMobile ? 168 : 158 }}
              >
                <Option value="createdAt-desc">最新添加</Option>
                <Option value="createdAt-asc">最早添加</Option>
                <Option value="year-desc">年份降序</Option>
                <Option value="year-asc">年份升序</Option>
                <Option value="title-asc">标题 A-Z</Option>
              </Select>
              {selectedRowKeys.length > 0 && (
                <Dropdown menu={{ items: batchCategoryMenuItems }} trigger={['click']}>
                  <Button className="lit-toolbar-btn" icon={<FolderOutlined />}>
                    批量归类 ({selectedRowKeys.length}) <DownOutlined />
                  </Button>
                </Dropdown>
              )}
              {selectedRowKeys.length > 0 && (
                <Dropdown menu={{ items: batchExportMenuItems }} trigger={['click']}>
                  <Button className="lit-toolbar-btn" icon={<DownloadOutlined />}>
                    导出引用 ({selectedRowKeys.length}) <DownOutlined />
                  </Button>
                </Dropdown>
              )}
              <Button
                className="lit-toolbar-btn"
                icon={<SettingOutlined />}
                onClick={() => setCategoryManagerVisible(true)}
                title="分类管理"
              >
                分类管理
              </Button>
            </div>
          </div>
        </div>

      <div className="lit-ant-table-wrap lit-lib-list-bg">

      {isMobile ? (
        <List
          dataSource={literatures}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, page, limit: pageSize }));
            }
          }}
          renderItem={(record: Literature) => {
            const catId = (record as any).categoryId as string | null | undefined;
            const categoryName = getCategoryName(catId);
            const statusColors: Record<string, string> = {
              '未读': 'default',
              '阅读中': 'processing',
              '已读': 'success',
              '精读': 'warning'
            };
            const statusOptions = [
              { key: '未读', label: '未读', color: 'default' },
              { key: '阅读中', label: '阅读中', color: 'processing' },
              { key: '已读', label: '已读', color: 'success' },
              { key: '精读', label: '精读', color: 'warning' }
            ];
            const statusMenuItems: MenuProps['items'] = statusOptions.map(option => ({
              key: option.key,
              label: (
                <span>
                  <Tag color={option.color} style={{ margin: 0 }}>
                    {option.label}
                  </Tag>
                </span>
              ),
              onClick: () => {
                if (option.key !== record.readStatus) {
                  handleUpdateReadStatus(record._id, option.key);
                }
              }
            }));

            const categoryMenuItems = [
              {
                key: 'null',
                label: '未分类',
                onClick: () => handleSingleCategory(record._id, null)
              },
              ...categories.map(category => ({
                key: category._id,
                label: (
                  <span>
                    <FolderOutlined style={{ color: category.color, marginRight: 4 }} />
                    {category.name}
                  </span>
                ),
                onClick: () => handleSingleCategory(record._id, category._id)
              }))
            ];

            return (
              <List.Item>
                <Card
                  size="small"
                  title={
                    <Button
                      type="link"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => {
                        setInspectorSelectedId(record._id);
                        setInspectorDrawerOpen(true);
                      }}
                    >
                      {record.title}
                    </Button>
                  }
                  extra={
                    <Dropdown menu={{ items: buildLiteratureActionMenu(record) }} trigger={['click']} placement="bottomRight">
                      <Button type="text" icon={<MoreOutlined />} size="small" />
                    </Dropdown>
                  }
                >
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ color: '#666' }}>{record.authors?.slice(0, 2).join(', ') || '-'}</span>
                      <span style={{ color: '#999' }}>{record.year || '-'}</span>
                      {categoryName ? (
                        <Tag color={categories.find(c => c._id === catId)?.color || '#1890ff'}>
                          <FolderOutlined style={{ marginRight: 4 }} />
                          {categoryName}
                        </Tag>
                      ) : (
                        <Tag>未分类</Tag>
                      )}
                      <Dropdown menu={{ items: statusMenuItems }} trigger={['click']} placement="bottomLeft">
                        <Tag color={statusColors[record.readStatus] || 'default'} style={{ cursor: 'pointer' }}>
                          {record.readStatus} <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                        </Tag>
                      </Dropdown>
                    </div>
                    {literatureHasPdfAttachment(record) ? (
                      <Button
                        type="default"
                        size="small"
                        className="lit-stitch-btn-secondary"
                        icon={<EyeOutlined />}
                        block
                        onClick={() => navigate(`/literature/${record._id}/view`)}
                      >
                        查看 PDF
                      </Button>
                    ) : null}
                  </Space>
                </Card>
              </List.Item>
            );
          }}
        />
      ) : (
        <>
          <Spin spinning={loading && literatures.length > 0}>
          <div className="lit-lib-card-grid">
            {loading && literatures.length === 0 ? (
              <Typography.Paragraph type="secondary" style={{ gridColumn: '1 / -1' }}>
                加载中…
              </Typography.Paragraph>
            ) : null}
            {!loading && literatures.length === 0 ? (
              <Typography.Paragraph type="secondary" style={{ gridColumn: '1 / -1' }}>
                暂无文献，请调整筛选或添加条目。
              </Typography.Paragraph>
            ) : null}
            {literatures.map((record) => {
              const authorLine = record.authors?.slice(0, 2).join(', ') || '—';
              const journalLine = record.journal || '—';
              const snippet = literatureListCardNotesPreview(record);
              const selected = record._id === inspectorSelectedId;
              return (
                <article
                  key={record._id}
                  role="button"
                  tabIndex={0}
                  className={`lit-lib-card${selected ? ' lit-lib-card--selected' : ''}`}
                  onClick={(e) => {
                    const el = e.target as HTMLElement;
                    if (el.closest('.ant-checkbox-wrapper')) return;
                    if (el.closest('.ant-dropdown-trigger')) return;
                    if (el.closest('.lit-lib-card__actions')) return;
                    if (el.closest('button')) return;
                    setInspectorSelectedId(record._id);
                    setInspectorDrawerOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setInspectorSelectedId(record._id);
                      setInspectorDrawerOpen(true);
                    }
                  }}
                >
                  <div className="lit-lib-card__top">
                    <div className="lit-lib-card__top-left">
                      <Checkbox
                        checked={selectedRowKeys.includes(record._id)}
                        onChange={(ev) => {
                          ev.stopPropagation();
                          toggleCardSelected(record._id, ev.target.checked);
                        }}
                        onClick={(ev) => ev.stopPropagation()}
                        aria-label="勾选用于批量操作"
                      />
                      <span className={readStatusPillClass(record.readStatus)}>{record.readStatus || '未读'}</span>
                    </div>
                    <div className="lit-lib-card__top-right">
                      <span className="lit-lib-card__time">{formatLiteratureRelativeTime(record.updatedAt)}</span>
                      <Dropdown menu={{ items: buildLiteratureActionMenu(record) }} trigger={['click']} placement="bottomRight">
                        <Button type="text" size="small" icon={<MoreOutlined />} title="更多" onClick={(ev) => ev.stopPropagation()} />
                      </Dropdown>
                    </div>
                  </div>
                  <h3 className="lit-lib-card__title">{record.title}</h3>
                  {snippet ? <p className="lit-lib-card__snippet">{snippet}</p> : <p className="lit-lib-card__snippet">暂无笔记</p>}
                  <div className="lit-lib-card__meta">
                    <span className="lit-lib-card__meta-item">
                      <LimsNavIcon name="person" sizePx={16} />
                      {authorLine}
                    </span>
                    <span className="lit-lib-card__meta-item">
                      <LimsNavIcon name="menu_book" sizePx={16} />
                      {journalLine}
                      {record.year != null ? ` · ${record.year}` : ''}
                    </span>
                  </div>
                  {literatureHasPdfAttachment(record) ? (
                    <div className="lit-lib-card__actions" onClick={(ev) => ev.stopPropagation()}>
                      <Button
                        type="default"
                        size="small"
                        className="lit-stitch-btn-secondary lit-lib-card__pdf-btn"
                        icon={<EyeOutlined />}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          navigate(`/literature/${record._id}/view`);
                        }}
                      >
                        查看 PDF
                      </Button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
          </Spin>
          <div className="lit-lib-pagination-wrap">
            <Pagination
              current={pagination.page}
              pageSize={pagination.limit}
              total={pagination.total}
              showSizeChanger
              showTotal={(total) => `共 ${total} 篇文献`}
              onChange={(page, pageSize) => {
                setPagination((prev) => ({ ...prev, page, limit: pageSize ?? prev.limit }));
              }}
            />
          </div>
        </>
      )}
      </div>
      </div>

      <Drawer
        rootClassName="lit-inspector-drawer lit-inspector-drawer--stitch lit-inspector-drawer--no-header"
        title={null}
        closable={false}
        placement="right"
        width={
          typeof window !== 'undefined' ? (isMobile ? Math.min(440, window.innerWidth) : 450) : 450
        }
        open={inspectorDrawerOpen && !!inspectorSelectedId}
        onClose={() => setInspectorDrawerOpen(false)}
        destroyOnHidden={false}
        styles={{
          body: { padding: 0, background: '#f8f9fa' },
          header: { display: 'none' }
        }}
      >
        <LiteratureInspectorPanel
          literatureId={inspectorSelectedId}
          categories={categories}
          showSectionRail={false}
          onNavigate={(p) => {
            navigate(p);
            setInspectorDrawerOpen(false);
          }}
          onLiteratureUpdated={() => void fetchLiteratures()}
          onSelectRelated={(id) => {
            setInspectorSelectedId(id);
            setInspectorDrawerOpen(true);
          }}
          onDeleted={() => {
            setInspectorDrawerOpen(false);
            setInspectorSelectedId(null);
            void fetchLiteratures();
          }}
        />
      </Drawer>
    </LiteratureLimsShell>
  );
};

export default LiteratureList;

